import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { X, Keyboard } from "lucide-react";

/**
 * KeyboardShortcutsOverlay
 * ──────────────────────────────────────────────────────────────────
 * Press "?" anywhere (outside an input) to toggle. Press "Esc" or
 * click the backdrop to close. Categories are organised so users can
 * scan quickly:
 *   • Navigation
 *   • Content
 *   • Project
 *
 * Hooked globally from App.js — renders nothing until opened.
 */
const CATEGORIES = [
  {
    name_en: "Navigation",
    name_ar: "التنقّل",
    items: [
      { keys: ["G", "H"],   label_en: "Go home",                  label_ar: "إلى الرئيسية" },
      { keys: ["G", "T"],   label_en: "Browse tutors",            label_ar: "تصفّح المدرّسين" },
      { keys: ["G", "D"],   label_en: "Go to my dashboard",       label_ar: "إلى لوحتي" },
      { keys: ["G", "M"],   label_en: "Messages",                 label_ar: "الرسائل" },
      { keys: ["G", "S"],   label_en: "Trust Score page",         label_ar: "صفحة نقاط الثقة" },
      { keys: ["/"],        label_en: "Focus search",             label_ar: "تركيز البحث" },
    ],
  },
  {
    name_en: "Content",
    name_ar: "المحتوى",
    items: [
      { keys: ["N"],        label_en: "New booking",              label_ar: "حجز جديد" },
      { keys: ["B"],        label_en: "Open TED assistant",       label_ar: "فتح مساعد TED" },
      { keys: ["L"],        label_en: "Toggle language",          label_ar: "تبديل اللغة" },
      { keys: ["T"],        label_en: "Toggle theme (dark/light)", label_ar: "تبديل الثيم" },
    ],
  },
  {
    name_en: "Project (admin)",
    name_ar: "المشروع (أدمن)",
    items: [
      { keys: ["G", "A"],   label_en: "Admin dashboard",          label_ar: "لوحة الأدمن" },
      { keys: ["G", "U"],   label_en: "Users list",               label_ar: "قائمة المستخدمين" },
      { keys: ["G", "E"],   label_en: "Email logs",               label_ar: "سجل الإيميلات" },
      { keys: ["G", "X"],   label_en: "Trust Authority",          label_ar: "Trust Authority" },
      { keys: ["G", "P"],   label_en: "Payment events",           label_ar: "سجل المدفوعات" },
    ],
  },
  {
    name_en: "Help",
    name_ar: "مساعدة",
    items: [
      { keys: ["?"],        label_en: "Open this overlay",        label_ar: "فتح هذه الواجهة" },
      { keys: ["Esc"],      label_en: "Close any overlay",        label_ar: "إغلاق الواجهة" },
    ],
  },
];

const isTypingTarget = (el) => {
  if (!el) return false;
  const tag = (el.tagName || "").toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select" || el.isContentEditable;
};

export default function KeyboardShortcutsOverlay({ isAr = false }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const lastFocusedRef = useRef(null);

  // Close on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Global key listener
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && open) {
        e.preventDefault();
        setOpen(false);
        return;
      }
      // "?" → Shift + / on most keyboards, also raw "?"
      const isQuestion = e.key === "?" || (e.shiftKey && e.key === "/");
      if (!isQuestion) return;
      if (isTypingTarget(e.target)) return;
      e.preventDefault();
      lastFocusedRef.current = document.activeElement;
      setOpen((o) => !o);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Restore focus on close
  useEffect(() => {
    if (!open && lastFocusedRef.current && lastFocusedRef.current.focus) {
      try { lastFocusedRef.current.focus(); } catch (_) { /* ignore */ }
    }
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={isAr ? "اختصارات لوحة المفاتيح" : "Keyboard shortcuts"}
      dir={isAr ? "rtl" : "ltr"}
      data-testid="keyboard-shortcuts-overlay"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-md"
        onClick={() => setOpen(false)}
        data-testid="keyboard-shortcuts-backdrop"
      />

      {/* Card */}
      <div
        className="relative w-full max-w-3xl max-h-[88vh] overflow-auto rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] shadow-2xl"
        style={{ animation: "kbdPop 240ms cubic-bezier(0.16,1,0.3,1)" }}
      >
        <style>{`
          @keyframes kbdPop {
            from { opacity: 0; transform: translateY(8px) scale(0.985); }
            to   { opacity: 1; transform: translateY(0)   scale(1); }
          }
        `}</style>

        <header className="flex items-center justify-between gap-3 px-6 py-4 border-b border-[var(--border-color)] sticky top-0 bg-[var(--bg-secondary)]">
          <div className="flex items-center gap-2.5">
            <span className="w-7 h-7 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)] flex items-center justify-center">
              <Keyboard className="w-3.5 h-3.5 text-[var(--accent-primary)]" />
            </span>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--text-tertiary)]">
                {isAr ? "الاختصارات" : "Shortcuts"}
              </div>
              <h2 className="text-base font-semibold tracking-tight">
                {isAr ? "اختصارات لوحة المفاتيح" : "Keyboard shortcuts"}
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="inline-flex w-8 h-8 items-center justify-center rounded-full border border-[var(--border-color)] hover:border-[var(--accent-primary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            aria-label={isAr ? "إغلاق" : "Close"}
            data-testid="keyboard-shortcuts-close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {CATEGORIES.map((cat) => (
            <section key={cat.name_en} data-testid={`kbd-cat-${cat.name_en.toLowerCase().replace(/\W+/g, "-")}`}>
              <h3 className="text-[10px] uppercase tracking-[0.22em] text-[var(--text-tertiary)] font-semibold mb-3">
                {isAr ? cat.name_ar : cat.name_en}
              </h3>
              <ul className="space-y-2">
                {cat.items.map((row) => (
                  <li key={row.label_en} className="flex items-center justify-between gap-4 text-sm">
                    <span className="text-[var(--text-secondary)]">{isAr ? row.label_ar : row.label_en}</span>
                    <span className="inline-flex items-center gap-1 shrink-0">
                      {row.keys.map((k, i) => (
                        <kbd
                          key={`${k}-${i}`}
                          className="min-w-[26px] px-2 h-6 inline-flex items-center justify-center rounded-md border border-[var(--border-color)] bg-[var(--bg-primary)] text-[11px] font-semibold tracking-wide tabular-nums text-[var(--text-primary)] shadow-[inset_0_-2px_0_var(--border-color)]"
                        >
                          {k}
                        </kbd>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <footer className="px-6 py-3 border-t border-[var(--border-color)] text-[11px] text-[var(--text-tertiary)] flex items-center justify-between sticky bottom-0 bg-[var(--bg-secondary)]">
          <span>
            {isAr
              ? "اضغط ? في أي مكان لفتح/إغلاق هذه الواجهة"
              : "Press ? anywhere to toggle this overlay"}
          </span>
          <span className="inline-flex items-center gap-1">
            <kbd className="px-1.5 h-5 inline-flex items-center rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-[10px]">Esc</kbd>
            <span>{isAr ? "للإغلاق" : "to close"}</span>
          </span>
        </footer>
      </div>
    </div>
  );
}
